import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ProductVariantAutocomplete } from '@/components/products/ProductVariantAutocomplete';
import type { ProductVariantSearchResult } from '@/api/inventory-management/products/useSearchProductVariants';
import { useListPrices } from '@/api/rpc/hooks/accounts-receivable/useDiscounts';
import { SpinnerIcon } from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';

export interface AddLineItemData {
  quantity: number;
  unitPrice: number;
  unit?: string;
  selectedProductVariantId: string;
  addedInternally: boolean;
  /** Variant SKU + description, for callers that store display fields directly. */
  sku?: string;
  description?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: AddLineItemData) => void;
  isLoading?: boolean;
  customerId?: string;
}

export function AddLineItemModal({
  open,
  onClose,
  onSave,
  isLoading,
  customerId,
}: Props) {
  const gt = useGT();
  const [selectedVariant, setSelectedVariant] = useState<
    ProductVariantSearchResult | undefined
  >();
  const [quantity, setQuantity] = useState<number | undefined>();
  const [unitPrice, setUnitPrice] = useState<number | undefined>();
  // Track whether the user has manually edited the unit price so we don't
  // overwrite their input when the list price query resolves.
  const [userEditedPrice, setUserEditedPrice] = useState(false);

  const selectedSku = selectedVariant?.variantSku;

  const { data: listPriceData, isLoading: isListPriceLoading } = useListPrices(
    customerId && selectedSku ? { customerId, skus: [selectedSku] } : null,
  );

  const listPriceResult = selectedSku
    ? (listPriceData?.prices[selectedSku] ?? null)
    : null;

  // Resolve UOM: prefer the variant search result, fall back to price list line
  const resolvedUom = selectedVariant?.uom || listPriceResult?.uomCode || null;

  // Auto-populate unit price from list price when it arrives, but only
  // if the user hasn't manually changed the price field.
  // NOTE: This is one of the rare cases where useEffect is appropriate —
  // we're synchronising local form state with an async server response.
  useEffect(() => {
    if (listPriceResult?.listPrice != null && !userEditedPrice) {
      setUnitPrice(listPriceResult.listPrice);
    }
  }, [listPriceResult?.listPrice, userEditedPrice]);

  const handleVariantSelect = useCallback(
    (variant: ProductVariantSearchResult | undefined) => {
      setSelectedVariant(variant);
      // Reset price tracking when variant changes
      setUserEditedPrice(false);
      if (!variant) {
        setUnitPrice(undefined);
      }
    },
    [],
  );

  const handleUnitPriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUserEditedPrice(true);
      setUnitPrice(e.target.value ? Number(e.target.value) : undefined);
    },
    [],
  );

  const handleSave = useCallback(() => {
    if (!selectedVariant || !quantity || !unitPrice) return;

    onSave({
      quantity,
      unitPrice,
      unit: resolvedUom ?? undefined,
      selectedProductVariantId: selectedVariant.productVariantId,
      addedInternally: true,
      sku: selectedVariant.variantSku ?? undefined,
      description: selectedVariant.productDescription ?? undefined,
    });

    // Reset form
    setSelectedVariant(undefined);
    setQuantity(undefined);
    setUnitPrice(undefined);
    setUserEditedPrice(false);
  }, [selectedVariant, quantity, unitPrice, onSave, resolvedUom]);

  const handleClose = useCallback(() => {
    setSelectedVariant(undefined);
    setQuantity(undefined);
    setUnitPrice(undefined);
    setUserEditedPrice(false);
    onClose();
  }, [onClose]);

  const isValid =
    !!selectedVariant &&
    quantity != null &&
    quantity > 0 &&
    unitPrice != null &&
    unitPrice > 0;

  const totalAmount =
    quantity && unitPrice ? (quantity * unitPrice).toFixed(2) : '-';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            <T context="Sales order draft: add line item dialog title">
              Add Line Item
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="Sales order add line item dialog instructions">
              Search for a product by SKU or name, then enter quantity and unit
              price.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2 min-w-0">
          {/* Product variant search */}
          <div className="min-w-0 space-y-1.5">
            <Label>
              <T context="Required field label on add line item form">
                Product <span className="text-destructive">*</span>
              </T>
            </Label>
            <ProductVariantAutocomplete
              onVariantSelect={handleVariantSelect}
              selectedVariantId={selectedVariant?.productVariantId}
              className="min-w-0"
              customerId={customerId}
              popoverModal
            />
          </div>

          {/* Auto-populated read-only fields */}
          {selectedVariant && (
            <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm min-w-0">
              <div className="min-w-0">
                <span className="text-xs font-medium text-muted-foreground">
                  <T context="Product description label">Description</T>
                </span>
                <p
                  className="truncate"
                  title={selectedVariant.productDescription ?? undefined}
                >
                  {selectedVariant.productDescription ?? '-'}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  <T context="Unit of measure abbreviation">UOM</T>
                </span>
                <p>
                  {isListPriceLoading && !resolvedUom ? (
                    <SpinnerIcon className="inline-block h-3 w-3 animate-spin" />
                  ) : (
                    (resolvedUom ?? '-')
                  )}
                </p>
              </div>
            </div>
          )}

          {/* User-input fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-line-item-quantity">
                <T context="Required quantity field on add line item">
                  Quantity <span className="text-destructive">*</span>
                </T>
              </Label>
              <Input
                id="add-line-item-quantity"
                type="number"
                value={quantity ?? ''}
                onChange={(e) =>
                  setQuantity(
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder={gt('0', {
                  context: 'Numeric placeholder for quantity field',
                })}
                min={0}
                step={1}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-line-item-unit-price">
                <T context="Required unit price field on add line item">
                  Unit Price <span className="text-destructive">*</span>
                </T>
                {isListPriceLoading && selectedVariant && (
                  <SpinnerIcon className="ml-1 inline-block h-3 w-3 animate-spin" />
                )}
              </Label>
              <Input
                id="add-line-item-unit-price"
                type="number"
                value={unitPrice ?? ''}
                onChange={handleUnitPriceChange}
                placeholder={
                  isListPriceLoading
                    ? gt('Loading...', {
                        context: 'Placeholder while list price loads',
                      })
                    : gt('0.00', {
                        context: 'Numeric placeholder for unit price',
                      })
                }
                min={0}
                step={0.01}
              />
              {listPriceResult && !userEditedPrice && (
                <p className="text-xs text-muted-foreground">
                  <T context="Shows which price list the list price came from">
                    List price from{' '}
                    <Var>
                      {listPriceResult.priceListName ??
                        gt('price list', {
                          context: 'Fallback when price list name missing',
                        })}
                    </Var>
                  </T>
                </p>
              )}
            </div>
          </div>

          {/* Computed total */}
          {isValid && (
            <div className="flex items-center justify-between border-t pt-3 text-sm">
              <span className="font-medium text-muted-foreground">
                <T context="Line item total label">Total</T>
              </span>
              <span className="font-semibold">${totalAmount}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            <T context="Dismiss dialog">Cancel</T>
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isLoading}>
            <T context="Confirm add line item">Add Line Item</T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
